import { DEFAULT_QUERY_OPTIONS } from "@solomon-apps/shared";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import { fetchMetricsDaily, fetchMetricsKpis, type MetricsDaily, type MetricsKpis } from "../api/metricsApi";
import type { ChartConfig, ChartDataPoint, ShareCardMeta } from "../data/chartData";
import { CARD_TYPE_MAP } from "../data/chartData";
import { formatCurrency } from "../utils/formatters";

// --- Query hooks ---

function useMetricsKpis(): UseQueryResult<MetricsKpis> {
    return useQuery<MetricsKpis>({
        queryKey: ["metrics-kpis"],
        queryFn: ({ signal }) => fetchMetricsKpis(signal),
        ...DEFAULT_QUERY_OPTIONS
    });
}

function useMetricsDaily(): UseQueryResult<MetricsDaily> {
    return useQuery<MetricsDaily>({
        queryKey: ["metrics-daily"],
        queryFn: ({ signal }) => fetchMetricsDaily(signal),
        ...DEFAULT_QUERY_OPTIONS
    });
}

// --- Data transformation helpers ---

function formatDateLabel(dateStr: string): string {
    const [year, month, day] = dateStr.split("-");
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function buildCumulativeChart(
    daily: MetricsDaily,
    field: keyof Pick<MetricsDaily, "tradeVolumeDaily" | "lpFeesDaily" | "yaasDistributions">
): ChartDataPoint[] {
    const values = daily[field];
    let cumulative = 0;
    return daily.dates.map((date, i) => {
        cumulative += parseFloat(values[i]);
        return { date: formatDateLabel(date), value: cumulative };
    });
}

function buildSnapshotChart(
    daily: MetricsDaily,
    field: keyof Pick<MetricsDaily, "totalPositionsDaily">
): ChartDataPoint[] {
    const values = daily[field];
    return daily.dates.map((date, i) => ({
        date: formatDateLabel(date),
        value: parseFloat(values[i])
    }));
}

function computeDelta(data: ChartDataPoint[], daysBack = 7): string {
    if (data.length < daysBack + 1) return "+$0";
    const current = data[data.length - 1].value;
    const prior = data[data.length - 1 - daysBack].value;
    const diff = current - prior;
    const sign = diff >= 0 ? "+" : "-";
    return `${sign}${formatCurrency(Math.abs(diff))}`;
}

// --- Composite hook ---

interface MetricsChartsResult {
    primaryCharts: ChartConfig[];
    secondaryCharts: ChartConfig[];
    shareCardMeta: Record<string, ShareCardMeta>;
    isLoading: boolean;
}

export function useMetricsCharts(): MetricsChartsResult {
    const { data: kpis, isLoading: kpisLoading } = useMetricsKpis();
    const { data: daily, isLoading: dailyLoading } = useMetricsDaily();

    const isLoading = kpisLoading || dailyLoading;

    if (!kpis || !daily) {
        return {
            primaryCharts: [],
            secondaryCharts: [],
            shareCardMeta: {},
            isLoading
        };
    }

    const lpVolumeData = buildCumulativeChart(daily, "tradeVolumeDaily");
    const lpEarningsData = buildCumulativeChart(daily, "lpFeesDaily");
    const totalPositionsData = buildSnapshotChart(daily, "totalPositionsDaily");
    const yaasDistributionsData = buildCumulativeChart(daily, "yaasDistributions");

    const primaryCharts: ChartConfig[] = [
        {
            title: "LP Volume (Cumulative)",
            data: lpVolumeData,
            kpiValue: parseFloat(kpis.cumulativeVolume),
            formatter: formatCurrency
        },
        {
            title: "LP Earnings (Cumulative)",
            data: lpEarningsData,
            kpiValue: parseFloat(kpis.lpEarnings),
            formatter: formatCurrency
        }
    ];

    const secondaryCharts: ChartConfig[] = [
        {
            title: "Total Positions Using YaaS",
            data: totalPositionsData,
            kpiValue: parseFloat(kpis.totalPositions),
            formatter: formatCurrency
        }
        // {
        //     title: "YaaS Distributions (Cumulative)",
        //     data: yaasDistributionsData,
        //     kpiValue: parseFloat(kpis.yaasDistributions),
        //     formatter: formatCurrency
        // }
    ];

    const shareCardMeta: Record<string, ShareCardMeta> = {};
    for (const chart of [...primaryCharts, ...secondaryCharts]) {
        const cardType = CARD_TYPE_MAP[chart.title];
        if (cardType) {
            shareCardMeta[chart.title] = {
                cardType,
                delta: computeDelta(chart.data),
                deltaPeriod: "last week"
            };
        }
    }

    return { primaryCharts, secondaryCharts, shareCardMeta, isLoading };
}
